import React, { useMemo, useState } from 'react';
import { TextInputProps as RNTextInputProps } from 'react-native';
import { StyledInputs, StyledTextInputProps } from '../styled/form/Inputs';
import { StyledForms } from '../styled/form/Form';
import { useInput } from '@app/hooks/useInput';
import { normalize } from '@app/helpers';
import { worksansoxford } from '@app/style';
import { theme } from '@app/theme';
import { HelperText } from '../styled/form/Label';

export interface DateTimePickerProps extends RNTextInputProps, StyledTextInputProps {
  name: string;
  type?: "date" | "datetime-local";
  label: string;
  helper?: string;
  onWhite?: boolean;
}

export const DateTimePicker = ({ label, name, helper, type = "date", onWhite = false, ...props }: DateTimePickerProps) => {
  const { field, error, formState } = useInput(name);
  const [blur, setBlur] = useState(true);

  const value = useMemo(() => {
    if (!field.value) {
      return '';
    }

    const date = new Date(field.value);

    // Vérification de la validité de la date
    if (isNaN(date.getTime())) {
      return '';
    }

    if (type === 'date') {
      // Format pour input type="date" => YYYY-MM-DD
      return date.toISOString().split('T')[0];
    }

    if (type === 'datetime-local') {
      // Format pour input type="datetime-local" => YYYY-MM-DDTHH:mm
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    return date.toISOString();
  }, [type, field.value]);

  const calendarPickerIndicatorStyle = `
    input[type="date"]::-webkit-calendar-picker-indicator,
    input[type="time"]::-webkit-calendar-picker-indicator,
    input[type="datetime-local"]::-webkit-calendar-picker-indicator {
      color-scheme: ${onWhite ? 'white' : 'dark'};
    }
  `;

  return (
    <StyledInputs.Holder onWhite={onWhite}>
      {/* Injection du style pour le picker */}
      <style>{calendarPickerIndicatorStyle}</style>
      {!onWhite &&
        <StyledInputs.Label
          isFilled={onWhite ? !!value : true}
          isDirty={formState.isDirty}
          isBlurred={blur}
          onWhite={onWhite}>
          {label}
        </StyledInputs.Label>
      }
      {!!error && (
        <StyledForms.Error>
          <StyledForms.ErrorLabel>{error}</StyledForms.ErrorLabel>
        </StyledForms.Error>
      )}

      <input
        ref={field.ref}
        type={type}
        value={value}
        // @ts-expect-error
        style={{
          height: onWhite ? 34 :
            props.size === "large" ? normalize(92) :
              props.size === "small" ? normalize(58) :
                normalize(76),
          ...worksansoxford,
          fontSize: onWhite ? normalize(16) : normalize(21),
          fontWeight: '500',
          aspectRatio: props.round ? "1/1" : undefined,
          color: onWhite ? '#212529' : theme.colors.babypowder,
          textAlign: props.center ? 'center' : 'left',
          borderRadius: onWhite ? 22 : props.inside ? 0 : props.round ? 100 : 5,
          backgroundColor: onWhite ? '#fff' : props.size === "small" ? theme.colors.oxfordblue : 'rgba(240, 249 , 252, .04)',
          padding: props.nude ? 0 : onWhite ? normalize(8) : props.size === "small" ? normalize(21) : normalize(34),
          border: onWhite ? `1.5px solid ${theme.colors.celadonblue}` : 'none',
          outlineWidth: 0,
          borderBottomColor: props.nude ? 'transparent' : props.error ? theme.colors.crimson : props.isBlurred ? 'rgba(255,255,255,0)' : theme.colors.celadonblue,
          borderBottomWidth: onWhite ? 1.5 : props.nude ? 0 : 3,
          opacity: 1,
          width: onWhite ? 'auto' : '100%',
          zIndex: 999,
        }}
        {...props}
        onChange={(e) => {
          const inputValue = e.target.value;
          const date = new Date(inputValue);
          if (!inputValue || isNaN(date.getTime())) {
            // Si la date est invalide ou vide, on passe null
            field.onChange(null);
          } else {
            field.onChange(date.toISOString());
          }
        }}
        onBlur={() => setBlur(true)}
        onFocus={() => setBlur(false)}
      />

      {helper && (
        <HelperText>
          {helper}
        </HelperText>
      )}
    </StyledInputs.Holder>
  );
};
